<template>
  <div class="w-full h-full pt-5 relative">
    <BaseOrderModal
      @closeOrder="openedOrder = null"
      @orderStatusUpdated="loadOrders()"
      :orderUUID="openedOrder || ''"
      v-model="showOrderModal"
    />
    <div
      class="w-full flex flex-row items-center justify-between px-4 py-4 bg-gray-50"
    >
      <!-- Left side - Action buttons -->
      <div class="flex flex-row items-center gap-3">
        <a
          target="_blank"
          :href="unprintedOrdersLink"
          class="dark-button px-4 py-2"
          v-if="unprintedQty > 0"
        >
          Print unprinted orders ({{ unprintedQty }})
        </a>
        <div v-else class="text-gray-600">All orders were printed</div>
        <button
          class="warning-button px-4 py-2"
          @click="showPrintHistory = true"
          v-if="printBatches?.length > 0"
        >
          Print History
        </button>
        <button
          class="warning-button px-4 py-2"
          @click="showPrintHistoryAuto = true"
          v-if="printBatchesAuto.length > 0"
        >
          Print History Auto
        </button>
        <button
          class="text-blue-600 px-4 py-2 hover:bg-blue-50 rounded"
          @click="loadOrders()"
        >
          Refresh page
        </button>
      </div>

      <!-- Right side - Status circles -->
      <div class="flex flex-row items-center gap-6">
        <div class="flex flex-col items-center">
          <div
            class="w-20 h-20 rounded-full bg-blue-500 text-white flex items-center justify-center text-2xl font-bold shadow-lg"
          >
            {{ totalItems || orders?.length || 0 }}
          </div>
          <div class="text-sm text-gray-700 mt-2 text-center">
            Awaiting shipment
          </div>
        </div>
        <div class="flex flex-col items-center" v-if="fulfillmentAlert > 0">
          <div
            class="w-20 h-20 rounded-full bg-red-500 text-white flex items-center justify-center text-2xl font-bold shadow-lg animate-pulse"
          >
            {{ fulfillmentAlert }}
          </div>
          <div class="text-sm text-red-600 mt-2 text-center font-semibold">
            Alert status
          </div>
        </div>
      </div>
    </div>
    <div
      ref="optionsBox"
      id="options-box"
      v-if="showPrintHistory"
      class="
        absolute
        top-10
        z-50
        flex
        flex-col
        text-gray-600
        bg-white
        rounded-md
        py-10
        border-t
        border-b
        border-gray-200
        "
    >
      <div
        class="cursor-pointer ml-auto absolute top-2 right-2 rounded-full text-white bg-black w-6 h-6 flex justify-center items-center"
        @click="showPrintHistory = false"
      >
        x
      </div>
      <div class="w-full text-center mb-2">
        Print History
      </div>
      <a
        v-for="batch in printBatches"
        :key="batch.id"
        class="list-button flex flex-row"
        target="_blank"
        :href="`${batchLink}${batch.id}`"
      >
        Orders: {{ batch.ordersQty }}, printed {{ batch.date | timeAgo }}
        <img
          class="w-6 h-6 ml-2"
          src="@/assets/icons/printer.svg"
          alt="print"
        />
      </a>
    </div>
    <div
      ref="optionsBox"
      id="options-box"
      v-if="showPrintHistoryAuto"
      class="
        absolute
        top-10
        z-50
        flex
        flex-col
        text-gray-600
        bg-white
        rounded-md
        py-10
        border-t
        border-b
        border-gray-200
        "
    >
      <div
        class="cursor-pointer ml-auto absolute top-2 right-2 rounded-full text-white bg-black w-6 h-6 flex justify-center items-center"
        @click="showPrintHistoryAuto = false"
      >
        x
      </div>
      <div class="w-full text-center mb-2">
        Print History Auto
      </div>
      <a
        v-for="batch in printBatchesAuto"
        :key="`auto-${batch.id}`"
        class="list-button flex flex-row"
        target="_blank"
        :href="`${batchLink}${batch.id}`"
      >
        Orders: {{ batch.ordersQty }}, printed {{ batch.date | timeAgo }}
        <img
          class="w-6 h-6 ml-2"
          src="@/assets/icons/printer.svg"
          alt="print"
        />
      </a>
    </div>
    <OrdersFilters
      v-model="filters"
      :sellers="sellersInOrders"
      :shippingServices="shippingServicesInOrders"
      :statusAvailable="statusInOrders"
    />
    <div>
      <div class="bg-gray-100 rounded-md overflow-hidden px-2 py-6 mx-2">
        Orders not exported pending to be printed: {{ unprintedQtyNotExported }}
      </div>
      <div class="mx-2 mt-2">
        <div v-if="loading">
          Loading ...
        </div>
        <table
          v-else-if="orders && orders.length > 0"
          class="default-table text-xs"
        >
          <thead>
            <th>order number</th>
            <th>Status</th>
            <th style="width: 190px">Date</th>
            <th>Customer</th>
            <th>Products</th>
            <th>Shipping Service Requested</th>
            <th>Salesman</th>
            <th>Total</th>
            <th>Print</th>
          </thead>
          <tbody>
            <tr
              v-for="order in ordersFiltered"
              :key="order.uuid"
              :class="{
                'bg-green-success text-white': isSpecial(
                  order.shippingServiceRequested
                ),
                'bg-warning':
                  order.fulfillmentStatus == 0 &&
                  !isSpecial(order.shippingServiceRequested)
              }"
            >
              <td
                class="text-center font-bold"
                width="200px"
                @click="(openedOrder = order.uuid), (showOrderModal = true)"
              >
                {{ order.orderKey }}
                {{
                  order.orderKey !== order.orderNumber
                    ? `(${order.orderNumber})`
                    : ""
                }}
              </td>
              <td>{{ order.status }}</td>
              <td width="100px">{{ order.createDate | dateWithHours }}</td>
              <td>
                {{
                  order.customerEmail?.length > 20
                    ? order.customerEmail.substring(0, 20) + "..."
                    : order.customerEmail
                }}
              </td>
              <td>
                <!-- max 10 characters -->
                (<b>
                  {{ order.products?.split(",")?.length || "" }}
                </b>
                )
                {{
                  order.products?.length > 17
                    ? order.products.substring(0, 17) + "..."
                    : order.products
                }}
              </td>
              <td class="text-center">
                {{
                  order.shippingServiceRequested?.length > 20
                    ? order.shippingServiceRequested.substring(0, 20) + "..."
                    : order.shippingServiceRequested
                }}
              </td>
              <td>{{ order.salesman }}</td>
              <td>${{ order.total }}</td>
              <td>
                <div
                  class="text-center flex flex-row justify-center items-center"
                  :class="{ 'bg-red-400 p-2 rounded-md': order.printed == '0' }"
                >
                  <a
                    target="_blank"
                    :href="`${baseUrl}orders/print_invoice/${order.uuid}`"
                    class="flex h-4 w-4"
                  >
                    <img
                      class="w-full h-full"
                      src="@/assets/icons/printer.svg"
                      alt="print"
                    />
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else>Nothing found</div>
        <div
          v-if="!loading && totalPages > 1"
          class="flex flex-row items-center justify-center gap-4 py-4"
        >
          <button
            class="default-button px-4 py-2"
            :disabled="page <= 1"
            @click="previousPage"
          >
            Previous
          </button>
          <span class="text-gray-600">
            Page {{ page }} of {{ totalPages }} ({{ totalItems }} orders)
          </span>
          <button
            class="default-button px-4 py-2"
            :disabled="page >= totalPages"
            @click="nextPage"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  getOrdersWarehouse,
  getPrintBatches,
  getPrintBatchesAuto
} from "@/api/Orders.js";
import OrdersFilters from "@/components/OrdersFilters.vue";

export default {
  name: "OrdersPageWarehouse",
  data() {
    return {
      orders: [],
      loading: true,
      openedOrder: null,
      showOrderModal: false,
      windowIsFocused: true,
      showPrintHistory: false,
      showPrintHistoryAuto: false,
      printBatches: [],
      printBatchesAuto: [],
      page: 1,
      limitPerPage: 50,
      totalItems: 0,
      totalPages: 0,
      focusDebounceTimer: null,
      filters: {
        orderNumber: "",
        marketplaceId: "",
        salesman: "",
        customerEmail: "",
        customerInfoauto: {
          name: "",
          id: ""
        },
        status: "",
        shippingServiceRequested: "",
        product: ""
      }
    };
  },
  components: {
    OrdersFilters
  },
  computed: {
    departmentCode() {
      return this.$route.params.departmentCode;
    },
    statusInOrders() {
      return this.orders
        .map(order => order.status)
        .filter((value, index, self) => self.indexOf(value) === index);
    },
    shippingServicesInOrders() {
      return this.orders
        .map(order => order.shippingServiceRequested)
        .filter((value, index, self) => self.indexOf(value) === index);
    },
    sellersInOrders() {
      return this.orders
        .map(order => order.salesman)
        .filter((value, index, self) => self.indexOf(value) === index);
    },
    marketplaces() {
      return this.$store.getters["general/getMarketplaces"];
    },
    baseUrl() {
      return process.env.VUE_APP_NKRSYNC_BE;
    },
    unprintedQty() {
      return (
        this.orders.filter(
          order => order.printed == 0 && order.integratedSystemOrderId !== null
        )?.length || 0
      );
    },
    unprintedQtyNotExported() {
      return (
        this.orders.filter(
          order =>
            order.printed == 0 &&
            order.integratedSystemOrderId == null &&
            order.status == "awaiting_shipment"
        )?.length || 0
      );
    },
    unprintedOrdersLink() {
      return `${process.env.VUE_APP_NKRSYNC_BE}orders/print_not_printed_invoices?department=${this.departmentCode}`;
    },
    batchLink() {
      return `${process.env.VUE_APP_NKRSYNC_BE}orders/print_batch/`;
    },
    fulfillmentAlert() {
      return (
        this.orders.filter(order => order.fulfillmentStatus == 0)?.length || 0
      );
    },
    ordersFiltered() {
      return this.orders.filter(order => {
        return (
          (this.filters.orderNumber === "" ||
            order.orderNumber.includes(this.filters.orderNumber)) &&
          (this.filters.marketplaceId === "" ||
            order.marketplaceId.includes(this.filters.marketplaceId)) &&
          (this.filters.salesman === "" ||
            order.salesman.includes(this.filters.salesman)) &&
          (this.filters.customerEmail === "" ||
            order.customerEmail.includes(this.filters.customerEmail)) &&
          (this.filters.status === "" ||
            order.status.includes(this.filters.status)) &&
          (this.filters.shippingServiceRequested === "" ||
            order.shippingServiceRequested.includes(
              this.filters.shippingServiceRequested
            )) &&
          (this.filters.product === "" ||
            order.products.includes(this.filters.product))
        );
      });
    }
  },
  watch: {
    // Watch for changes in the departmentCode parameter
    departmentCode: {
      handler(newDepartmentCode, oldDepartmentCode) {
        if (newDepartmentCode !== oldDepartmentCode) {
          this.loadOrders();
        }
      },
      immediate: false
    }
  },
  mounted() {
    this.loadOrders();
    this.detectFocusOut();
    this.checkForOrderInUrl();
    this.setupServiceWorkerListener();
  },
  methods: {
    setupServiceWorkerListener() {
      if ("serviceWorker" in navigator) {
        this.swMessageHandler = event => {
          if (event.data && event.data.type === "REFRESH_ORDERS") {
            this.$toast.open({
              message: "New orders received, refreshing list...",
              type: "success",
              position: "top"
            });
            console.log(
              "[OrdersWarehouse] Refreshing orders from push notification"
            );
            this.loadOrders();
          }
        };
        navigator.serviceWorker.addEventListener(
          "message",
          this.swMessageHandler
        );
      }
    },
    checkForOrderInUrl() {
      const orderUUID = this.$route.query.orderOpen;
      if (orderUUID) {
        this.openedOrder = orderUUID;
        this.showOrderModal = true;
      }
    },
    isSpecial(shippingType) {
      const specials = ["counter", "local_delivery", "pick_up", "see_notes"];
      return specials.some(type => type == shippingType);
    },
    detectFocusOut() {
      this.windowIsFocused = false;
      window.addEventListener("focus", this.onWindowFocusChange);
      window.addEventListener("blur", this.onWindowFocusChange);
      window.addEventListener("pageshow", this.onWindowFocusChange);
      window.addEventListener("pagehide", this.onWindowFocusChange);
    },
    onWindowFocusChange(e) {
      if ({ focus: 1, pageshow: 1 }[e.type]) {
        if (this.windowIsFocused) return;
        this.windowIsFocused = true;
        clearTimeout(this.focusDebounceTimer);
        this.focusDebounceTimer = setTimeout(() => this.loadOrders(), 2000);
      } else if (this.windowIsFocused) {
        this.windowIsFocused = false;
      }
    },
    previousPage() {
      if (this.page > 1) {
        this.page--;
        this.loadOrders();
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++;
        this.loadOrders();
      }
    },
    async loadOrders() {
      try {
        const res = await getOrdersWarehouse(
          this.departmentCode || undefined,
          this.page,
          this.limitPerPage
        );
        this.orders = res.orders;
        this.totalItems = res.totalItems;
        this.totalPages = res.totalPages;
        this.loading = false;
        this.getPrintHistory();
      } catch (error) {
        this.loading = false;
        this.orders = [];
      }
    },
    getPrintHistory() {
      getPrintBatches(this.departmentCode).then(data => {
        this.printBatches = data.batches;
      });
      getPrintBatchesAuto(this.departmentCode).then(data => {
        this.printBatchesAuto = data.batches;
      });
    }
  },
  beforeDestroy() {
    clearTimeout(this.focusDebounceTimer);
    window.removeEventListener("focus", this.onWindowFocusChange);
    window.removeEventListener("blur", this.onWindowFocusChange);
    window.removeEventListener("pageshow", this.onWindowFocusChange);
    window.removeEventListener("pagehide", this.onWindowFocusChange);
    if (this.swMessageHandler && "serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener(
        "message",
        this.swMessageHandler
      );
    }
  }
};
</script>

<style lang="postcss" scoped>
.list-button {
  @apply w-full border-white border p-4 text-gray-600 hover:bg-gray-400 hover:text-white;
}
</style>
